import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getUserInfo } from '@/api/api'
import Cookies from 'js-cookie'
import './index.less'

type ConnectStep = 'loading' | 'login-check' | 'ready' | 'authorizing' | 'success' | 'error'

const ConnectGbase = () => {
  const { t } = useTranslation()
  const [step, setStep] = useState<ConnectStep>('loading')
  const [message, setMessage] = useState('')
  const [gbaseUrl, setGbaseUrl] = useState('')
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  useEffect(() => {
    initializeGbaseConnection()
  }, [])

  const initializeGbaseConnection = async () => {
    try {
      console.log('Initializing GBase connection page')
      console.log('Current URL:', window.location.href)

      // 解析URL参数获取GBase授权地址
      const urlParams = new URLSearchParams(window.location.search)
      const gbaseUrlParam = urlParams.get('gbase-url')

      if (!gbaseUrlParam) {
        throw new Error(t('gbase.connect.error.missing.url'))
      }

      // 解码GBase URL（支持Base64编码）
      let decodedGbaseUrl: string
      try {
        // 首先尝试Base64解码
        decodedGbaseUrl = atob(gbaseUrlParam)
        console.log('GBase URL decoded from Base64:', decodedGbaseUrl)
      } catch {
        // 如果Base64解码失败，直接使用原始URL
        decodedGbaseUrl = decodeURIComponent(gbaseUrlParam)
        console.log('GBase URL decoded from URL encoding:', decodedGbaseUrl)
      }

      setGbaseUrl(decodedGbaseUrl)

      // 检查登录状态
      setStep('login-check')
      await new Promise(resolve => setTimeout(resolve, 500))

      const token = Cookies.get(import.meta.env.VITE_COOKIE_SUFFIX as string)
      if (token) {
        try {
          const { data } = await getUserInfo()
          if (data.status === 200) {
            setIsLoggedIn(true)
            setStep('ready')
            console.log('User is logged in, ready for GBase authorization')
          } else {
            throw new Error('Invalid token')
          }
        } catch (error) {
          console.log('Token validation failed, user needs to login')
          setIsLoggedIn(false)
          setStep('ready')
        }
      } else {
        console.log('No token found, user needs to login')
        setIsLoggedIn(false)
        setStep('ready')
      }

    } catch (error) {
      console.error('GBase connection initialization error:', error)
      const errorMessage = error instanceof Error ? error.message : t('gbase.connect.error.generic')
      setMessage(errorMessage)
      setStep('error')
    }
  }

  const handleLogin = () => {
    console.log('Redirecting to login page')
    const loginUrl = new URL(`${import.meta.env.VITE_FELO_API_URL}/login`)
    loginUrl.searchParams.append('app_id', 'subtitle')
    loginUrl.searchParams.append('app_source', 'desktop_gbase')
    // 将 redirect_uri 放在 hash 中以避免 URL 过长导致 414 错误
    loginUrl.hash = `redirect_uri=${encodeURIComponent(window.location.href)}`
    window.location.href = loginUrl.toString()
  }

  const handleAuthorize = () => {
    try {
      setStep('authorizing')
      setMessage(t('gbase.connect.authorizing.desc'))
      
      console.log('Starting GBase authorization with original URL:', gbaseUrl)
      
      // 直接使用原始的GBase授权URL，不做任何修改
      // 该URL已经包含了所有必要的参数，包括正确的redirectUrl
      console.log('Using GBase authorization URL as-is:', gbaseUrl)
      
      // 直接跳转到GBase授权页面
      window.location.href = gbaseUrl
      
    } catch (error) {
      console.error('GBase authorization error:', error)
      const errorMessage = error instanceof Error ? error.message : t('gbase.connect.error.authorize')
      setMessage(errorMessage)
      setStep('error')
    }
  }

  const getStepTitle = () => {
    switch (step) {
      case 'loading': return t('gbase.connect.loading')
      case 'login-check': return t('gbase.connect.login.checking')
      case 'ready': return isLoggedIn ? t('gbase.connect.ready') : t('gbase.connect.login.required')
      case 'authorizing': return t('gbase.connect.authorizing')
      case 'success': return t('gbase.connect.success')
      case 'error': return t('gbase.connect.error')
      default: return ''
    }
  }

  const getStepDescription = () => {
    switch (step) {
      case 'loading': return t('gbase.connect.loading.desc')
      case 'login-check': return t('gbase.connect.login.checking.desc')
      case 'ready': 
        return isLoggedIn 
          ? t('gbase.connect.ready.desc')
          : t('gbase.connect.login.required.desc')
      case 'authorizing': return message || t('gbase.connect.authorizing.desc')
      case 'success': return message
      case 'error': return message
      default: return ''
    }
  }

  return (
    <div className="gbase-gradient-bg min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        {/* Main Card */}
        <div className="gbase-card">
          {/* Logo and Brand */}
          <div className="mb-8">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">{t('gbase.connect.title')}</h1>
            <p className="text-gray-500 mt-2 text-sm">Felo Subtitles × GBase</p>
          </div>

          {/* Loading State */}
          {(step === 'loading' || step === 'login-check') && (
            <div className="space-y-6">
              <div className="relative">
                <div className="w-12 h-12 mx-auto">
                  <div className="absolute inset-0 rounded-full border-3 border-gray-200"></div>
                  <div className="absolute inset-0 rounded-full border-3 border-transparent border-t-blue-500 gbase-spinner"></div>
                </div>
              </div>
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <p className="text-gray-600">{getStepDescription()}</p>
              </div>
            </div>
          )}

          {/* Ready State */}
          {step === 'ready' && (
            <div className="space-y-6">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <div className="text-center space-y-3">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <p className="text-gray-600">{getStepDescription()}</p>
              </div>

              {/* Authorization Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <h3 className="font-medium text-gray-800 mb-3 text-left">{t('gbase.connect.info.title')}</h3>
                <ul className="text-sm text-gray-600 text-left space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span>{t('gbase.connect.info.point1')}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span>{t('gbase.connect.info.point2')}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span>{t('gbase.connect.info.point3')}</span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="gbase-btn-container">
                {!isLoggedIn ? (
                  <>
                    <button 
                      onClick={handleLogin}
                      className="gbase-btn-primary"
                    >
                      <svg className="w-4 h-4 gbase-icon-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <span>{t('gbase.connect.login')}</span>
                    </button>
                    <button 
                      onClick={() => window.close()} 
                      className="gbase-btn-secondary"
                    >
                      {t('gbase.connect.cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={handleAuthorize}
                      className="gbase-btn-primary"
                    >
                      <svg className="w-4 h-4 gbase-icon-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span>{t('gbase.connect.authorize')}</span>
                    </button>
                    <button 
                      onClick={() => window.close()} 
                      className="gbase-btn-secondary"
                    >
                      {t('gbase.connect.cancel')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Authorizing State */}
          {step === 'authorizing' && (
            <div className="space-y-6">
              <div className="w-12 h-12 mx-auto relative">
                <div className="w-12 h-12 border-3 border-gray-200 rounded-full"></div>
                <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-t-blue-500 rounded-full gbase-spinner"></div>
              </div>
              
              <div className="text-center space-y-3">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <p className="text-gray-600">{getStepDescription()}</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700 text-center">{t('gbase.connect.authorizing.redirect')}</p>
              </div>
            </div>
          )}
          
          {/* Error State */}
          {step === 'error' && (
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              
              <div className="text-center space-y-3">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{getStepDescription()}</p>
                </div>
              </div>
              
              <div className="gbase-btn-container">
                <button 
                  onClick={() => window.location.reload()} 
                  className="gbase-btn-primary"
                >
                  <svg className="w-4 h-4 gbase-icon-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{t('gbase.connect.retry')}</span>
                </button>
                <button 
                  onClick={() => window.close()} 
                  className="gbase-btn-secondary"
                >
                  {t('gbase.connect.close')}
                </button>
              </div>
            </div>
          )}

          {/* Brand Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-center space-x-2 text-gray-400">
              <span className="text-xs">Felo Subtitles</span>
              <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
              <span className="text-xs">GBase</span>
            </div>
            <div className="text-xs text-gray-300 mt-2">{t('gbase.connect.security')}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConnectGbase